import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { NavIcon, iconForPath } from './icons';
import './BottomBar.css';

type NavItem = { path: string; label: string };
type NavGroup = { heading: string; items: NavItem[] };

/**
 * Mobilny dolny pasek: 2–3 najczęstsze czynności danej roli + Menu (sheet).
 * Serwisant/admin integratora zaczynają od Zgłoszeń (praca reaktywna),
 * klient — od Maszyn i Dokumentacji. Reszta sekcji żyje w sheecie.
 */
const BottomBar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isAdmin = user?.roles?.includes('ADMIN');
  const isCustomer = user?.roles?.includes('CUSTOMER');
  const isEmployeeAdmin = user?.roles?.includes('EMPLOYEE_ADMIN');

  // Close the sheet on route change.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  // Primary bar actions per role (max 3 + Menu).
  const primary: NavItem[] = isAdmin
    ? [
        { path: '/machines', label: 'Maszyny' },
        { path: '/my-tickets', label: 'Zgłoszenia' },
      ]
    : isEmployeeAdmin
      ? [
          { path: '/my-tickets', label: 'Zgłoszenia' },
          { path: '/machines', label: 'Maszyny' },
          { path: '/admin/assignments', label: 'Przypisania' },
        ]
      : isCustomer
        ? [
            { path: '/machines', label: 'Maszyny' },
            { path: '/documentation', label: 'Dokumentacja' },
          ]
        : [
            // EMPLOYEE — serwisant w terenie
            { path: '/my-tickets', label: 'Zgłoszenia' },
            { path: '/machines', label: 'Maszyny' },
            { path: '/notes', label: 'Notatki' },
          ];

  // Sheet sections per role (grouped like the desktop sidebar).
  const extraGroups: NavGroup[] = isAdmin
    ? [
        {
          heading: 'Sekcje',
          items: [
            { path: '/customers', label: 'Klienci' },
            { path: '/documentation', label: 'Dokumentacja' },
            { path: '/notes', label: 'Notatki' },
            { path: '/admin/integrators', label: 'Integratorzy' },
            { path: '/admin/billing', label: 'Plany i faktury' },
            { path: '/admin/assignments', label: 'Przypisania' },
            { path: '/admin/stats', label: 'Statystyki' },
            { path: '/admin/ai-costs', label: 'Koszty AI' },
            { path: '/admin/indexing', label: 'Indeksacje' },
            { path: '/admin/users', label: 'Użytkownicy' },
            { path: '/admin/settings', label: 'Ustawienia aplikacji' },
            { path: '/admin/gdpr', label: 'RODO' },
          ],
        },
      ]
    : isEmployeeAdmin
      ? [
          {
            heading: 'Praca',
            items: [
              { path: '/customers', label: 'Klienci' },
              { path: '/documentation', label: 'Dokumentacja' },
              { path: '/notes', label: 'Notatki' },
            ],
          },
          {
            heading: 'Zespół',
            items: [
              { path: '/team/members', label: 'Pracownicy' },
              { path: '/team/invitations', label: 'Zaproszenia' },
              { path: '/team/subscription', label: 'Abonament' },
              { path: '/admin/stats', label: 'Statystyki' },
            ],
          },
          {
            heading: 'Firma',
            items: [{ path: '/company-account', label: 'Konto firmy' }],
          },
        ]
      : isCustomer
        ? [
            {
              heading: 'Sekcje',
              items: [{ path: '/company-account', label: 'Konto firmy' }],
            },
          ]
        : [
            {
              heading: 'Sekcje',
              items: [
                { path: '/documentation', label: 'Dokumentacja' },
                { path: '/company-account', label: 'Konto firmy' },
              ],
            },
          ];

  const roleLabel = isAdmin ? 'Administrator' : isCustomer ? 'Klient' : isEmployeeAdmin ? 'Admin integratora' : 'Integrator';

  return (
    <>
      <nav className="bottombar" aria-label="Menu mobilne">
        {primary.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`bottombar-btn ${isActive(item.path) ? 'active' : ''}`}
          >
            <span className="bottombar-btn-indicator" />
            <span className="bottombar-btn-label">{item.label}</span>
            <NavIcon name={iconForPath(item.path)} size={18} className="bottombar-btn-icon" />
          </Link>
        ))}
        <button
          type="button"
          className={`bottombar-btn ${menuOpen ? 'active' : ''}`}
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="dialog"
          aria-expanded={menuOpen}
        >
          <span className="bottombar-btn-indicator" />
          <span className="bottombar-btn-label">{menuOpen ? 'Zamknij' : 'Menu'}</span>
          <NavIcon name={menuOpen ? 'close' : 'menu'} size={18} className="bottombar-btn-icon" />
        </button>
      </nav>

      {menuOpen && (
        <div
          className="sheet-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setMenuOpen(false)}
        >
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-handle" />

            {user && (
              <Link to="/profile" className="sheet-user">
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt="Avatar"
                    style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <span className="sheet-user-fallback">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </span>
                )}
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span className="sheet-user-name">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="sheet-user-role">
                    {roleLabel}
                  </span>
                </span>
              </Link>
            )}

            {user && (
              <div className="sheet-bell">
                <span className="sheet-bell-label">Powiadomienia</span>
                <NotificationBell />
              </div>
            )}

            {extraGroups.map((group) => (
              <div key={group.heading} className="sheet-section">
                <div className="sheet-heading">{group.heading}</div>
                {group.items.map((l) => (
                  <Link
                    key={l.path}
                    to={l.path}
                    className={`sheet-link ${isActive(l.path) ? 'active' : ''}`}
                  >
                    <NavIcon name={iconForPath(l.path)} size={16} className="sheet-link-icon" />
                    {l.label}
                  </Link>
                ))}
              </div>
            ))}

            <div className="sheet-section">
              <div className="sheet-heading">Konto</div>
              <Link to="/profile" className="sheet-link">
                <NavIcon name="user" size={16} className="sheet-link-icon" />
                Twoje konto
              </Link>
              <Link to="/regulamin" className="sheet-link">
                <NavIcon name="docs" size={16} className="sheet-link-icon" />
                Regulamin
              </Link>
              <Link to="/polityka-prywatnosci" className="sheet-link">
                <NavIcon name="shield" size={16} className="sheet-link-icon" />
                Polityka prywatności
              </Link>
              <Link to="/change-password" className="sheet-link">
                <NavIcon name="key" size={16} className="sheet-link-icon" />
                Zmień hasło
              </Link>
              <button type="button" onClick={handleLogout} className="sheet-link sheet-link-danger">
                <NavIcon name="logout" size={16} className="sheet-link-icon" />
                Wyloguj
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomBar;
